import React from 'react';
import { Checkbox, Empty, Spin } from 'antd';
import { AutoSizer, Table, Column } from 'react-virtualized';
import { useSelector } from 'react-redux';
import { arrayMoveImmutable } from 'array-move';
import { useTranslation } from 'react-i18next';
import { DndContext, DragEndEvent } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { ApplicationState } from '../../../reducers';
import { DragRow } from '../../global/dragableTable/DragRow';
import { DragHandle } from '../../global/dragableTable/DragHandle';

const DRAG_COLUMN_WIDTH = 50;

type WareHouseDistributorTableProps = {
  selectedWareHouseIds: number[];
  handleWDSelection: (selectedIds: number[]) => void;
  filterKeywords: string;
  wareHouseDistributorList: {
    record_number: number;
    id: number;
    name: string;
  }[];
};

const WareHouseDistributorTable: React.FC<WareHouseDistributorTableProps> = props => {
  const { t } = useTranslation();

  const fetchingReceiverWarehouseList = useSelector(
    (state: ApplicationState) => state.receiver.receivers.fetchingReceiverWarehouseList
  );

  const [distributorsData, setDistributorsData] = React.useState(props.wareHouseDistributorList);

  const filterdData = distributorsData.filter(wd =>
    wd.name.toLowerCase().includes(props.filterKeywords.toLowerCase())
  );

  React.useEffect(() => {
    setDistributorsData(props.wareHouseDistributorList);
  }, [props.wareHouseDistributorList]);

  const onDragEnd = ({ active, over }: DragEndEvent) => {
    if (active.id !== over?.id) {
      const activeIndex = filterdData.findIndex(record => record.record_number === active?.id);
      const overIndex = filterdData.findIndex(record => record.record_number === over?.id);
      const increasingIndex = activeIndex > overIndex;
      const smallIndex = increasingIndex ? overIndex : activeIndex;
      const bigIndex = increasingIndex ? activeIndex : overIndex;
      // update record_numbers
      const updatedRecord = filterdData.map(distributor => {
        if (distributor.record_number === activeIndex + 1) {
          return { ...distributor, record_number: overIndex + 1 };
        }
        if (
          smallIndex + 1 <= distributor.record_number &&
          bigIndex + 1 >= distributor.record_number
        ) {
          return {
            ...distributor,
            record_number: increasingIndex
              ? distributor.record_number + 1
              : distributor.record_number - 1,
          };
        }
        return distributor;
      });
      const sortedDistributorsData = arrayMoveImmutable(updatedRecord, activeIndex, overIndex);
      setDistributorsData(sortedDistributorsData);
      if (props.selectedWareHouseIds.length > 0) {
        props.handleWDSelection(
          sortedDistributorsData
            .filter(wD => props.selectedWareHouseIds.includes(wD.id))
            .map(wD => wD.id)
        );
      }
    }
  };

  const noData = () => {
    if (fetchingReceiverWarehouseList) return <Spin spinning className="mt-4" size="large" />;

    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
  };

  return (
    <AutoSizer>
      {({ width, height }) => (
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext
            items={filterdData.map(i => i.record_number)}
            strategy={verticalListSortingStrategy}
          >
            <Table
              lockAxis="y"
              width={width}
              height={height}
              headerHeight={48}
              rowHeight={40}
              rowCount={filterdData.length}
              rowRenderer={props => {
                const { key, ...restProps } = props;
                return (
                  <DragRow
                    key={key}
                    {...restProps}
                    columns={props.columns}
                    rowId={filterdData[props.index].record_number}
                  />
                );
              }}
              rowGetter={({ index }: { index: any }) => filterdData[index]}
              noRowsRenderer={noData}
            >
              <Column
                width={DRAG_COLUMN_WIDTH}
                dataKey="id"
                cellRenderer={() => {
                  return <DragHandle />;
                }}
              />
              <Column label={t('companyProfile:priority')} width={60} dataKey="record_number" />
              <Column
                flexGrow={1}
                label={t('companyProfile:warehouseDistributor')}
                dataKey="name"
                width={width / 2}
              />
              <Column
                label={t('companyProfile:selected')}
                width={60}
                dataKey="id"
                cellRenderer={({ rowData }) => {
                  return (
                    <span>
                      {rowData.id && (
                        <Checkbox
                          key={rowData.id}
                          checked={props.selectedWareHouseIds.includes(rowData.id)}
                          onChange={() => {
                            if (props.selectedWareHouseIds.includes(rowData.id)) {
                              const filterdIds = props.selectedWareHouseIds.filter(
                                wdId => wdId !== rowData.id
                              );
                              props.handleWDSelection(
                                distributorsData
                                  .filter(dD => filterdIds.includes(dD.id))
                                  .map(dD => dD.id)
                              );
                            } else {
                              const allIds = [...props.selectedWareHouseIds, rowData.id];
                              props.handleWDSelection(
                                distributorsData
                                  .filter(dD => allIds.includes(dD.id))
                                  .map(dD => dD.id)
                              );
                            }
                          }}
                        />
                      )}
                    </span>
                  );
                }}
              />
            </Table>
          </SortableContext>
        </DndContext>
      )}
    </AutoSizer>
  );
};

export default WareHouseDistributorTable;
